import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useOrgStore } from '@/stores/orgStore';
import { AuthService } from '@/services/AuthService';
import { AlertTriangle } from 'lucide-react';

const authService = new AuthService();
const PAID_PLAN_KEYS = new Set(['monthly', 'yearly', 'plan_monthly', 'plan_yearly', 'starter', 'alpha', 'alpha_expired']);

/**
 * Persistent banner shown to logged-in users with unverified email.
 * Cannot be permanently dismissed — only disappears when email is verified.
 */
export function EmailVerificationBanner() {
  const user = useAuthStore((s) => s.user);
  const fetchMe = useAuthStore((s) => s.fetchMe);

  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [cooldown, setCooldown] = React.useState(0);
  const [checking, setChecking] = React.useState(false);
  const [notYet, setNotYet] = React.useState(false);

  // Cooldown timer
  React.useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((c) => Math.max(0, c - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Clear "not yet" message after 5 seconds
  React.useEffect(() => {
    if (!notYet) return;
    const timer = setTimeout(() => setNotYet(false), 5000);
    return () => clearTimeout(timer);
  }, [notYet]);

  // Hide on onboarding — user just signed up, let them finish setup first
  const { pathname } = useLocation();
  const isOnboarding = pathname.startsWith('/onboarding');

  // Hide for trialing/active subscribers — credit card is stronger identity verification
  const orgs = useOrgStore((s) => s.orgs);
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const activeOrg = orgs.find((o) => o.id === activeOrgId);
  const hasPaidPlan = PAID_PLAN_KEYS.has(activeOrg?.plan_key ?? '');

  // Set CSS custom property so sticky headers (AppHeader, sidebar) can offset below this banner
  const visible = !!(user && !user.isEmailVerified && !user.isSuperAdmin && !isOnboarding && !hasPaidPlan);
  React.useEffect(() => {
    if (visible) {
      document.documentElement.style.setProperty('--verification-banner-h', '2.5rem');
    } else {
      document.documentElement.style.setProperty('--verification-banner-h', '0px');
    }
    return () => {
      document.documentElement.style.setProperty('--verification-banner-h', '0px');
    };
  }, [visible]);

  // Don't render if no user, or already verified, or super admin
  if (!visible) return null;

  const handleResend = async () => {
    if (sending || cooldown > 0) return;
    setSending(true);
    setError(null);
    setSent(false);
    setNotYet(false);
    try {
      const result = await authService.resendVerification();
      if (result.sent) {
        setSent(true);
        setCooldown(60);
      } else {
        setError('Could not send email. Please try again later.');
      }
    } catch {
      setError('Failed to resend. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleCheckNow = async () => {
    if (checking) return;
    setChecking(true);
    setError(null);
    setNotYet(false);
    try {
      await fetchMe(true);
      // If fetchMe succeeds and user is now verified, the component will unmount.
      // If still here after a tick, user is still unverified.
      setTimeout(() => {
        // Component may have unmounted if verification succeeded
        setChecking(false);
        setNotYet(true);
      }, 300);
    } catch {
      setChecking(false);
      setError('Could not check status. Please try again.');
    }
  };

  return (
    <div className="bg-warning-50 border-b border-warning-200 px-4 py-2.5 flex items-center justify-center gap-3 text-sm flex-shrink-0 sticky top-0 z-50">
      <AlertTriangle className="w-4 h-4 text-warning-600 flex-shrink-0" />
      <span className="text-warning-800">
        Please verify your email address to start creating.
      </span>

      <div className="flex items-center gap-2 ml-1">
        <button
          onClick={handleResend}
          disabled={sending || cooldown > 0}
          className="text-warning-700 hover:text-warning-900 font-medium underline underline-offset-2 disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
        >
          {sending
            ? 'Sending...'
            : cooldown > 0
              ? `Resend (${cooldown}s)`
              : 'Resend email'}
        </button>

        <span className="text-warning-300">|</span>

        <button
          onClick={handleCheckNow}
          disabled={checking}
          className="text-warning-700 hover:text-warning-900 font-medium underline underline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {checking ? 'Checking...' : "I've verified"}
        </button>
      </div>

      {sent && (
        <span className="text-success-700 text-xs ml-1">Sent! Check your inbox.</span>
      )}
      {notYet && (
        <span className="text-error-600 text-xs ml-1">Not verified yet — click the link in your email first.</span>
      )}
      {error && (
        <span className="text-error-600 text-xs ml-1">{error}</span>
      )}
    </div>
  );
}
